import React, { ReactNode, useEffect, cloneElement, ReactElement, SetStateAction, Dispatch, useState } from 'react';
import classNames from 'classnames';
import {
  flip,
  offset,
  shift,
  size,
  hide as hideMiddleware,
  autoUpdate,
  useFloating,
  useInteractions,
  useRole,
  useClick,
  useDismiss,
  FloatingPortal,
  FloatingFocusManager,
} from '@floating-ui/react';
import { getZIndex } from '../../styles';
import { FLOATING_ROOT_ID } from '../../constants';
import { TIME_PICKER_COLUMN_HEIGHT, TIME_PICKER_SAFE_AREA } from './TimePickerOptions';

type ContentWithCloseAction = (action: { close(): void; availableHeight: number }) => ReactNode;

export interface TimePickerOverlayProps {
  /** @desc reference element를 구할 수 있는 컴포넌트여야 합니다. */
  children: React.ReactNode;
  className?: string;
  content?: ContentWithCloseAction;
  /** @default getZIndex('picker') */
  zIndex?: number;
  opened?: boolean;
  disabled?: boolean;
  fill?: boolean;
  /** @default false */
  usePortal?: boolean;
  onClose?: () => void;
  onOpen?: () => void;
  onCloseOutsidePress?: () => void;
  setOpened: Dispatch<SetStateAction<boolean>>;
}

// TODO(@olive-sally): 공용 overlay 컴포넌트화
export const TimePickerOverlay = ({
  children,
  className,
  content,
  zIndex = getZIndex('picker'),
  opened: openProp,
  disabled,
  fill,
  usePortal = false,
  onClose,
  onOpen,
  onCloseOutsidePress,
  setOpened,
}: TimePickerOverlayProps) => {
  const [currentAvailableHeight, setCurrentAvailableHeight] = useState(
    TIME_PICKER_COLUMN_HEIGHT + TIME_PICKER_SAFE_AREA,
  );
  const handleClose = () => {
    setOpened(false);
    onClose?.();
  };

  const handleOpen = (open: boolean) => {
    setOpened(open);
    !open && onCloseOutsidePress?.();
  };

  const {
    refs,
    floatingStyles,
    context,
    middlewareData: { hide },
  } = useFloating({
    open: openProp,
    onOpenChange: handleOpen,
    whileElementsMounted: autoUpdate,
    placement: 'bottom-start',
    middleware: [
      offset(4),
      shift(),
      flip(),
      size({
        apply: ({ rects, elements, availableHeight }) => {
          setCurrentAvailableHeight(availableHeight);
          Object.assign(elements.floating.style, {
            width: fill ? `${rects.reference.width}px` : 'auto',
            maxHeight: `${Math.floor(availableHeight)}px`,
          });
        },
      }),
      hideMiddleware(),
    ],
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context, { enabled: !disabled }),
    useRole(context, { role: 'listbox' }),
    useDismiss(context),
  ]);

  const renderFloatingContent = () => {
    return (
      <FloatingFocusManager context={context} initialFocus={-1}>
        <div
          {...getFloatingProps({
            className: classNames('pds-time-picker-overlay', className),
            ref: refs.setFloating,
            style: {
              ...floatingStyles,
              zIndex,
              display: hide?.referenceHidden ? 'none' : 'block',
            },
          })}
        >
          {content?.({ close: handleClose, availableHeight: currentAvailableHeight })}
        </div>
      </FloatingFocusManager>
    );
  };

  useEffect(() => {
    openProp ? onOpen?.() : onClose?.();
  }, [openProp]);

  return (
    <>
      {cloneElement(
        children as ReactElement,
        getReferenceProps({ ref: refs.setReference, ...(children as ReactElement).props }),
      )}
      {openProp &&
        (usePortal ? (
          <FloatingPortal id={FLOATING_ROOT_ID}>{renderFloatingContent()}</FloatingPortal>
        ) : (
          renderFloatingContent()
        ))}
    </>
  );
};
